import * as esri from 'esri-leaflet';
import { GIS_PORTAL_BASE } from '@app/modules/gis/gis-config';
import { getGisToken, getGisTokenSync } from '@app/modules/gis/gis-token';

/**
 * esri-leaflet issues its requests through its own XHR/JSONP layer, so the
 * Angular HttpInterceptor never sees them. Callers also have no reliable way
 * to attach the token: `esri.query({ url, token })` is silently ignored
 * because Task only reads a token supplied through `.token()`.
 *
 * This patch injects a fresh GIS token into every esri-leaflet Task and
 * Service request targeting the GIS portal origin, so tokens are applied
 * (and refreshed) centrally rather than at each call site.
 */

const GIS_ORIGIN = new URL(GIS_PORTAL_BASE).origin;

let patched = false;

function isGisUrl(url: string): boolean {
  try {
    return new URL(url, window.location.origin).origin === GIS_ORIGIN;
  } catch {
    return false;
  }
}

export function applyEsriTokenPatch(): void {
  if (patched) { return; }
  patched = true;

  const taskProto = (esri as any).Task.prototype;
  const originalTaskRequest = taskProto.request;

  taskProto.request = function (callback: any, context: any) {
    const url = this._service ? this._service.options.url : this.options.url;
    if (!isGisUrl(url)) {
      return originalTaskRequest.call(this, callback, context);
    }

    const token = getGisTokenSync();
    if (token) {
      this.params.token = token;
      return originalTaskRequest.call(this, callback, context);
    }

    // Token expired — wait for the silent refresh before firing the request.
    getGisToken().then(refreshed => {
      if (refreshed) { this.params.token = refreshed; }
      originalTaskRequest.call(this, callback, context);
    });
    return this;
  };

  const serviceProto = (esri as any).Service.prototype;
  const originalServiceRequest = serviceProto._request;

  serviceProto._request = function (method: string, path: string, params: any, callback: any, context: any) {
    if (!isGisUrl(this.options.url)) {
      return originalServiceRequest.call(this, method, path, params, callback, context);
    }

    const token = getGisTokenSync();
    if (token) {
      this.options.token = token;
      return originalServiceRequest.call(this, method, path, params, callback, context);
    }

    getGisToken().then(refreshed => {
      if (refreshed) { this.options.token = refreshed; }
      originalServiceRequest.call(this, method, path, params, callback, context);
    });
    return undefined;
  };
}
